import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { B } from '../constants';
import useDebouncedDestSearch from '../hooks/useDebouncedDestSearch';
import { destinations as destinationsAPI } from '../api';
import { useCurrency } from '../CurrencyContext';
import { borderRadius, transitions } from '../designTokens';
import Button from '../components/ui/Button';
import { GBWordmark, GBIcon } from '../components/Logo';
import DestinationCard from '../components/DestinationCard';
import Footer from '../components/Footer';
import Input from '../components/ui/Input';
import EmptyState from '../components/ui/EmptyState';
import { trackActivity } from '../lib/activityEvents';
import { SkeletonCard } from '../components/ui/LoadingState';
import PromoStrip from '../components/PromoStrip';

const HOME_RESPONSIVE_CSS = `
@media (max-width: 768px) {
  .home-section-title { font-size: clamp(24px, 5vw, 38px) !important; }
}
`;

const GRID_LIMIT = 10;
const HERO_SUGGESTION_LIMIT = 6;

// Search hits without a real city id can't render DetailScreen, so they open the planner instead.
const hasCityId = (d) => (Number.isInteger(d.cityId) && d.cityId > 0)
  || /^gb:city:\d+$/.test(String(d.id ?? ''))
  || (Number.isInteger(Number(d.id)) && Number(d.id) > 0);


export default function HomeScreen({ destinations, saved, onToggleSave, goScreen, loading, user, onAuthRequired }) {
  const { t } = useTranslation();
  const { formatPrice } = useCurrency();
  const gridRef = useRef(null);

  const [search, setSearch] = useState('');
  const { results: searchResults, loading: searchLoading, error: searchError } = useDebouncedDestSearch(search, {
    fetchEmpty: false,
    surface: 'home_explore',
    cacheScope: 'all',
    fetcher: (q) => destinationsAPI.search(q, { all: true }),
  });
  const isSearching = search.trim().length > 0;
  const [visibleCount, setVisibleCount] = useState(GRID_LIMIT);
  useEffect(() => { setVisibleCount(GRID_LIMIT); }, [search]);

  const [heroQuery, setHeroQuery] = useState('');
  const [heroOpen, setHeroOpen] = useState(false);
  const { results: heroResults, loading: heroLoading } = useDebouncedDestSearch(heroQuery, { fetchEmpty: false, surface: 'home_hero' });
  const heroSuggestions = heroQuery.trim().length >= 1 ? heroResults.slice(0, HERO_SUGGESTION_LIMIT) : [];

  const results = Array.isArray(destinations) ? destinations : [];

  const gridItems = isSearching ? searchResults : results.slice(0, visibleCount);

  const scrollToGrid = () => gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const browseAll = () => { setSearch(''); scrollToGrid(); };

  const planFromHero = (dest) => { setHeroOpen(false); goScreen('planner', { prefillDestination: dest }); };
  const submitHero = () => {
    const q = heroQuery.trim();
    if (q) planFromHero(q);
    else scrollToGrid();
  };

  return (
    <div style={{ background: B.sandPatternSoft }}>
      <style>{HOME_RESPONSIVE_CSS}</style>
      {/* Hero */}
      <div style={{ position: 'relative', height: 'clamp(400px, 70vh, 580px)' }}>
        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', borderRadius: 'inherit' }}>
          <img src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1600&q=80" alt="Travel" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: '15% center' }} />
          <div className="gradient-overlay-animated" style={{ position: 'absolute', inset: 0 }} />
        </div>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '0 24px' }}>
          <div style={{ marginBottom: 24 }}><GBWordmark size="lg" /></div>
          <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12, letterSpacing: 5, textTransform: 'uppercase', marginBottom: 16, fontFamily: "'Montserrat',sans-serif", fontWeight: 300 }}>{t('home.tagline')}</p>
          <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 'clamp(30px,5vw,62px)', color: 'white', fontWeight: 700, lineHeight: 1.15, marginBottom: 32, maxWidth: 660 }}>
            {t('home.heroTitle')}
          </h1>
          <div style={{ position: 'relative', width: '100%', maxWidth: 520 }}>
            <div className="hero-search" style={{ background: 'white', borderRadius: borderRadius.lg, display: 'flex', alignItems: 'center', padding: '6px 6px 6px 22px', width: '100%', boxSizing: 'border-box', boxShadow: '0 8px 32px rgba(0,0,0,0.25)', gap: 8, transition: `box-shadow ${transitions.normal}` }}
              role="search" aria-label="Search destinations">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={B.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
              <Input placeholder={t('home.searchPlaceholder')} value={heroQuery}
                onChange={e => { setHeroQuery(e.target.value); setHeroOpen(true); }}
                onFocus={() => setHeroOpen(true)}
                onBlur={() => setHeroOpen(false)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submitHero(); } else if (e.key === 'Escape') setHeroOpen(false); }}
                enterKeyHint="search"
                autoComplete="off"
                aria-label="Search destinations"
                aria-autocomplete="list"
                aria-expanded={heroOpen && heroSuggestions.length > 0}
                aria-controls="hero-suggestions"
                className="hero-search-input"
                style={{ flex: 1, border: 'none', fontSize: 15, color: B.night, background: 'transparent', fontFamily: "'Montserrat',sans-serif", outline: 'none', boxSizing: 'border-box', cursor: 'text' }} />
              <Button variant="primary" size="md" onClick={submitHero} aria-label="Search for destinations">
                {t('common.search')}
              </Button>
            </div>
            {heroOpen && heroSuggestions.length > 0 && (
              <ul id="hero-suggestions" role="listbox" aria-label="Destination suggestions"
                onMouseDown={e => e.preventDefault()}
                style={{ position: 'absolute', top: '100%', left: 0, right: 0, margin: '6px 0 0', padding: 6, listStyle: 'none', background: 'white', borderRadius: 16, border: '1px solid #e5e0d8', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', zIndex: 100, maxHeight: 320, overflowY: 'auto', textAlign: 'left' }}>
                {heroSuggestions.map((d, i) => {
                  const [city, ...rest] = String(d.name || '').split(',');
                  const region = rest.join(',').trim();
                  return (
                    <li key={d.id || d.placeId || i} role="option" aria-selected={false}
                      onClick={() => { trackActivity('search', 'search_result_clicked', { type: d.type, id: d.id, position: i }); planFromHero(d); }}
                      onMouseEnter={e => { e.currentTarget.style.background = B.sand; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 12, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: B.night, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{city.trim()}</div>
                        {region && <div style={{ fontSize: 12, color: B.muted }}>{region}</div>}
                      </div>
                      <span aria-hidden="true" style={{ color: B.muted, fontSize: 14 }}>→</span>
                    </li>
                  );
                })}
              </ul>
            )}
            {heroOpen && heroLoading && heroSuggestions.length === 0 && heroQuery.trim().length >= 1 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 6, padding: '12px 16px', background: 'white', borderRadius: 16, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', zIndex: 100, fontFamily: "'Montserrat',sans-serif", fontSize: 13, color: B.muted, textAlign: 'left' }}>
                Searching…
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Trust Bar */}
      <div style={{ background: B.sandPatternSoft, padding: '32px 20px', textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 24, flexWrap: 'wrap', alignItems: 'center', fontFamily: "'Montserrat',sans-serif", fontSize: 13, color: B.muted }}>
          <span><strong style={{ color: B.forest }}>Real, verified</strong> places only</span>
          <span style={{ color: '#d4cfc6' }}>·</span>
          <span><strong style={{ color: B.forest }}>Day-by-day</strong> maps & routes</span>
          <span style={{ color: '#d4cfc6' }}>·</span>
          <button
            type="button"
            aria-label="Free to start — view plans"
            onClick={() => goScreen('subscription')}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); goScreen('subscription'); } }}
            onMouseEnter={e => { e.currentTarget.firstChild.style.textDecoration = 'underline'; }}
            onMouseLeave={e => { e.currentTarget.firstChild.style.textDecoration = 'none'; }}
            style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", fontSize: 13, color: B.muted }}
          >
            <span style={{ textUnderlineOffset: 3 }}><strong style={{ color: B.forest }}>Free</strong> to start</span>
          </button>
        </div>
      </div>

      {/* Explore: destinations grid */}
      <div ref={gridRef} style={{ padding: '48px clamp(16px, 4vw, 40px)', scrollMarginTop: 80 }}>
        <div style={{ marginBottom: 24 }}>
          <p style={{ color: B.forest, fontSize: 11, letterSpacing: 4, textTransform: 'uppercase', fontWeight: 700, fontFamily: "'Montserrat',sans-serif", marginBottom: 8 }}>{t('discover.title')}</p>
          <h2 className="home-section-title" style={{ fontFamily: "'Playfair Display',serif", fontSize: 'clamp(28px, 5vw, 42px)', fontWeight: 700, marginBottom: 6 }}>{t('explore.title')}</h2>
          <p style={{ color: B.muted, fontSize: 15, fontFamily: "'Montserrat',sans-serif" }}>{t('explore.subtitle')}</p>
        </div>

        <div role="search" aria-label="Search explore destinations" style={{ maxWidth: 520, marginBottom: 16 }}>
          <Input
            enterKeyHint="search"
            placeholder={t('explore.searchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            onClear={() => setSearch('')}
            clearable
            radius="pill"
            aria-label="Search explore destinations"
            leftIcon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={B.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>}
            style={{ fontFamily: "'Montserrat',sans-serif", background: 'white' }}
          />
        </div>


        <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, color: B.muted, marginBottom: 20 }}>
          {isSearching
            ? `${searchResults.length} destination${searchResults.length !== 1 ? 's' : ''} found for "${search.trim()}"`
            : `Showing ${Math.min(visibleCount, results.length)} of ${results.length} destination${results.length !== 1 ? 's' : ''}`}
        </p>

        {loading || (isSearching && searchLoading) ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(min(280px, 100%),1fr))', gap: 24 }}>
            {[1,2,3,4,5,6].map(i => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : isSearching ? (
          searchError ? (
            <EmptyState icon="⚠️" title={searchError} description="Search could not be completed right now." />
          ) : searchResults.length > 0 ? (
            <>
            {searchResults[0].nearMatch && (
              <p role="status" style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 14, color: B.night, margin: '-8px 0 16px' }}>
                “{searchResults[0].nearMatch.query}” isn’t a separate destination yet. It’s part of the {String(searchResults[0].name || '').split(',')[0]} area
                {Number.isFinite(searchResults[0].nearMatch.km) ? ` (about ${searchResults[0].nearMatch.km} km away)` : ''}.
              </p>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(240px, 1fr))', gap: 24 }}>
              {gridItems.map((dest, i) => (
                <DestinationCard key={dest.id || dest.placeId || i} destination={dest} isSaved={saved.some(d => d.id === dest.id)} onToggleSave={onToggleSave} onClick={() => {
                  if (isSearching) trackActivity('search', 'search_result_clicked', { type: dest.type, id: dest.id, position: i });
                  if (hasCityId(dest)) goScreen('detail', dest); else goScreen('planner', { prefillDestination: dest });
                }} delay={i * 0.07} showPartners={false} />
              ))}
            </div>
            </>
          ) : (
            <EmptyState
              icon="🔍"
              title="No destinations found"
              description={`Try a different search term for "${search.trim()}".`}
            />
          )
        ) : results.length > 0 ? (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(240px, 1fr))', gap: 24 }}>
              {gridItems.map((dest, i) => (
                <DestinationCard key={dest.id || dest.placeId || i} destination={dest} isSaved={saved.some(d => d.id === dest.id)} onToggleSave={onToggleSave} onClick={() => goScreen('detail', dest)} delay={i * 0.07} showPartners={false} />
              ))}
            </div>
            {results.length > visibleCount && (
              <div style={{ textAlign: 'center', marginTop: 28 }}>
                <Button variant="secondary" size="md" onClick={() => setVisibleCount(results.length)}>
                  Show more ({results.length - visibleCount}) →
                </Button>
              </div>
            )}
          </>
        ) : (
          <EmptyState
            icon="🌍"
            title={t('explore.noResults')}
            description="Try a different search or browse all destinations."
            action={{ label: 'Show all', onClick: browseAll }}
            variant="withIllustration"
          />
        )}
      </div>

      {/* Reels Strip */}
      <div style={{ padding: 'clamp(32px, 5vw, 48px) clamp(16px, 3vw, 40px)', background: B.sandPatternSoft }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20 }}>
          <div>
            <p style={{ color: B.forest, fontSize: 11, letterSpacing: 4, textTransform: 'uppercase', fontWeight: 700, fontFamily: "'Montserrat',sans-serif", marginBottom: 8 }}>Short videos</p>
            <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 32, fontWeight: 700 }}>Travel Reels</h2>
          </div>
          <Button variant="secondary" size="sm" onClick={() => goScreen('reels')}>
            See all reels →
          </Button>
        </div>
        <PromoStrip user={user} label="" onAuthRequired={onAuthRequired} onPlanTrip={(p) => goScreen('ai', { prefillDestination: p.destination })} />
      </div>

      {/* Continue Planning */}
      {user && Array.isArray(saved) && saved.length > 0 && (
        <div style={{ padding: 'clamp(32px, 5vw, 48px) clamp(16px, 3vw, 40px)', background: 'white' }}>
          <div style={{ marginBottom: 24 }}>
            <p style={{ color: B.forest, fontSize: 11, letterSpacing: 4, textTransform: 'uppercase', fontWeight: 700, fontFamily: "'Montserrat',sans-serif", marginBottom: 8 }}>Pick up where you left off</p>
            <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 32, fontWeight: 700 }}>Continue Planning</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 20 }}>
            {saved.slice(0, 3).map(dest => (
              <div key={dest.id} onClick={() => goScreen('detail', dest)}
                style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 14, borderRadius: borderRadius.lg, border: '1px solid #e7e2d9', cursor: 'pointer', transition: `all ${transitions.normal}`, background: 'white' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = B.forest; e.currentTarget.style.boxShadow = '0 2px 12px rgba(15,25,35,0.08)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e7e2d9'; e.currentTarget.style.boxShadow = 'none'; }}>
                <img src={dest.img} alt={dest.name} style={{ width: 56, height: 56, borderRadius: 12, objectFit: 'cover', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 16, fontWeight: 700, color: B.night }}>{dest.name.split(',')[0]}</div>
                  <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: B.muted, marginTop: 2 }}>
                    {[dest.tag, dest.days ? `${dest.days} days` : null, formatPrice(dest.budget) || 'no price data']
                      .filter(Boolean).join(' · ')}
                  </div>
                </div>
                <span style={{ color: B.muted, fontSize: 18 }}>→</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Creator Earnings */}
      <div style={{ padding: '64px clamp(16px, 4vw, 40px)', textAlign: 'center', background: 'white' }}>
        <p style={{ color: B.forest, fontSize: 11, letterSpacing: 4, textTransform: 'uppercase', fontWeight: 700, fontFamily: "'Montserrat',sans-serif", marginBottom: 10 }}>Monetize your travels</p>
        <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 34, fontWeight: 700, marginBottom: 12 }}>
          Earn on <em style={{ color: B.amber }}>every booking</em>
        </h2>
        <p style={{ color: B.muted, fontSize: 15, maxWidth: 500, margin: '0 auto 40px', fontFamily: "'Montserrat',sans-serif" }}>
          Share your trips as reels and earn a partner commission when travelers book through them.
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 'clamp(20px, 4vw, 60px)', flexWrap: 'wrap', maxWidth: 720, margin: '0 auto' }}>
          {[
            { step: '1', icon: <svg color="white" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="2.5" /><path d="M7 2v20M17 2v20M2 12h20M2 7h5M2 17h5M17 7h5M17 17h5" /></svg>, title: 'Post a Reel', desc: 'Create short travel videos with your favorite destinations' },
            { step: '2', icon: <svg color="white" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>, title: 'Link Your Trip', desc: 'Attach your itinerary or booking link to each reel' },
            { step: '3', icon: <svg color="white" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M8.5 12.2l2.4 2.4 4.6-5.2" /></svg>, title: 'Get Paid', desc: 'Earn a commission when someone books through your link' },
          ].map(({ step, icon, title, desc }) => (
            <div key={step} style={{ flex: '1 1 200px', maxWidth: 240, textAlign: 'center', position: 'relative' }}>
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: `linear-gradient(135deg,${B.forest},${B.mint})`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', boxShadow: '0 4px 20px rgba(26,107,74,0.3)' }}>
                {icon}
              </div>
              <div style={{ position: 'absolute', top: 0, right: '50%', transform: 'translateX(50%)', background: B.amber, color: 'white', width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, fontFamily: "'Montserrat',sans-serif" }}>{step}</div>
              <h3 style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 16, fontWeight: 700, marginBottom: 6, color: B.night }}>{title}</h3>
              <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, color: B.muted, lineHeight: 1.5 }}>{desc}</p>
            </div>
          ))}
        </div>
        <Button variant="primary" size="lg" onClick={() => goScreen('reels')} style={{ marginTop: 36 }} aria-label="Start earning as a creator">
          Start Earning →
        </Button>
      </div>

      {/* CTA */}
      <div style={{ padding: '72px clamp(16px, 4vw, 40px)', textAlign: 'center', background: B.sandPatternSoft }}>
        <GBIcon size={60} />
        <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 36, marginTop: 22, marginBottom: 14 }}>
          {t('home.readyTitle')}
        </h2>
        <p style={{ color: B.muted, fontSize: 16, maxWidth: 440, margin: '0 auto 32px', fontFamily: "'Montserrat',sans-serif" }}>
          Browse popular destinations, save your favorites, and take your GreatBreak.
        </p>
        <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button variant="primary" size="lg" onClick={browseAll} aria-label="Explore destinations">
            {t('home.exploreDestinations')} →
          </Button>
          <Button variant="ghost" size="lg" onClick={() => { window.location.href = '/about'; }} style={{ border: `1.5px solid ${B.muted}`, color: B.muted }} aria-label="Learn more about GreatBreak">
            {t('common.learnMore')} →
          </Button>
        </div>
      </div>

      <Footer goScreen={goScreen} />
    </div>
  );
}
